import { useMemo, useState } from "react";
import { isCounterBooking, sttShort } from "./stt";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { appointmentQueryKeys, appointmentsApi } from "../../shared/api/appointments";
import { apiError } from "../../shared/api/client";
import {
  drillDownApi,
  drillDownQueryKeys,
  drillMetricLabel,
  parseDrill,
  NONE,
  type DrillKey,
  type DrillQuery,
} from "../../shared/api/drillDown";
import { appointmentChannelLabel } from "../../shared/api/reports";
import {
  BulkDeleteBar,
  runBulkDelete,
  useRowSelection,
} from "../../shared/ui/bulkSelect";
import { useDebounce } from "../../shared/hooks/useDebounce";
import { param, useUrlFilters } from "../../shared/hooks/useUrlFilters";
import {
  appointmentStatusLabel,
  formatDate,
  formatPhone,
  formatTime,
  isoDate,
  shiftIsoDate,
} from "../../shared/format";
import type {
  AppointmentListFilter,
  AppointmentStatus,
  AppointmentSummary,
} from "../../shared/types/appointment";
import { serviceNamesOf } from "../../shared/types/appointment";
import { PageHeader } from "../../shared/ui/PageHeader";
import { DateRangePicker } from "../../shared/ui/DateRangePicker";
import { PatientLink } from "../../shared/ui/PatientLink";
import { Toolbar } from "../../shared/ui/Toolbar";
import { DataTable, type Column } from "../../shared/ui/DataTable";
import { Pagination } from "../../shared/ui/Pagination";
import { AppointmentStatusBadge } from "./components/AppointmentStatusBadge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PAGE_SIZE = 25;

const STATUSES: readonly AppointmentStatus[] = [
  "REQUESTED",
  "CONFIRMED",
  "CHECKED_IN",
  "COMPLETED",
  "CANCELLED",
  "NO_SHOW",
];

export function AppointmentListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  // #19863 — link drill-down từ một con số: danh sách là ĐÚNG tập đã đếm, do server dựng lại.
  const drill = useMemo(() => parseDrill(params), [params]);
  const sel = useRowSelection();
  const [bulkPending, setBulkPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bộ lọc nằm trên URL (#19859): ô KPI trên Trang chủ link tới đây với `?date=&status=` (và
  // `doctorId=` cho số "của tôi"), nên danh sách mở ra phải mang ĐÚNG điều kiện đã đếm. `date` là
  // cách viết tắt của một khoảng một ngày; đổi khoảng trên màn hình thì bỏ nó đi. Giá trị lạ bị
  // bỏ qua. Mặc định: hôm nay → 7 ngày tới.
  const { filters, set } = useUrlFilters({
    q: param.string(),
    status: param.oneOf(STATUSES),
    doctorId: param.string(),
    date: param.isoDate(),
    fromDate: param.isoDate(() => isoDate(new Date())),
    toDate: param.isoDate(() => shiftIsoDate(new Date(), 7)),
    page: param.int(),
  });
  const { q, status, doctorId, page } = filters;
  const fromDate = filters.date || filters.fromDate;
  const toDate = filters.date || filters.toDate;
  const debouncedQ = useDebounce(q, 250);

  const filter: AppointmentListFilter = useMemo(
    () => ({
      q: debouncedQ || null,
      status: status || null,
      doctorId: doctorId || null,
      fromDate: fromDate || null,
      toDate: toDate || null,
    }),
    [debouncedQ, status, doctorId, fromDate, toDate]
  );

  const doctors = useQuery({
    queryKey: appointmentQueryKeys.doctors(),
    queryFn: () => appointmentsApi.listDoctors(),
  });

  const plain = useQuery({
    queryKey: appointmentQueryKeys.list(filter, page),
    queryFn: () =>
      appointmentsApi.list(filter, { page, size: PAGE_SIZE, sort: "startAt" }),
    placeholderData: (prev) => prev,
    enabled: !drill,
  });
  const drilled = useQuery({
    queryKey: drillDownQueryKeys.list(drill ?? ({} as DrillQuery), page),
    queryFn: () => drillDownApi.list(drill as DrillQuery, page, PAGE_SIZE),
    placeholderData: (prev) => prev,
    enabled: !!drill,
  });
  const list = drill ? drilled : plain;

  const columns: Column<AppointmentSummary>[] = [
    {
      // #19225 — STT theo phạm vi STT của phòng khám; "—" khi lịch chưa đủ chiều để có số.
      // 30/10: cột tên "STT quầy", ô chỉ in SỐ (không chữ "Quầy"); lịch đã vào phòng in số vào
      // phòng kèm dòng phụ "Phòng" để không đọc nhầm thành số quầy.
      key: "stt",
      header: "STT quầy",
      width: 96,
      render: (a) => (
        <div>
          <div className="font-semibold tabular-nums">{sttShort(a)}</div>
          {!isCounterBooking(a) && a.roomNo != null && (
            <div className="text-xs text-muted-foreground">Phòng</div>
          )}
        </div>
      ),
    },
    {
      key: "when",
      header: "Thời gian",
      width: 160,
      render: (a) => (
        <div>
          <div className="font-semibold">{formatTime(a.startAt)}</div>
          <div className="text-xs text-muted-foreground">{formatDate(a.startAt)}</div>
        </div>
      ),
    },
    {
      key: "patient",
      header: "Bệnh nhân",
      render: (a) => (
        <div>
          <PatientLink id={a.patientId} name={a.patientName} className="font-semibold" />
          {a.patientPhone && (
            <div className="text-xs text-muted-foreground">{formatPhone(a.patientPhone)}</div>
          )}
        </div>
      ),
    },
    {
      key: "doctor",
      header: "Bác sĩ",
      render: (a) => a.doctorName,
    },
    {
      key: "service",
      header: "Dịch vụ",
      render: (a) => {
        const names = serviceNamesOf(a);
        if (names.length === 0) return <span className="text-muted-foreground/60">—</span>;
        // Thời lượng là của CẢ lịch hẹn, không phải của dịch vụ cuối — nên đứng riêng một dòng.
        return (
          <div>
            <ul className="space-y-0.5" aria-label="Dịch vụ đã chọn">
              {names.map((name, i) => (
                <li key={`${i}-${name}`}>{name}</li>
              ))}
            </ul>
            <div className="text-xs text-muted-foreground">{a.durationMinutes} phút</div>
          </div>
        );
      },
    },
    {
      key: "status",
      header: "Trạng thái",
      width: 160,
      render: (a) => (
        <AppointmentStatusBadge status={a.status} inExam={a.inExam} />
      ),
    },
    {
      key: "actions",
      header: "",
      width: 72,
      ariaLabel: "Thao tác",
      stickyRight: true,
      render: (a) => (
        <Button
          type="button"
          variant="destructive"
          size="sm"
          onClick={async (e) => {
            e.stopPropagation();
            if (!confirm("Xoá vĩnh viễn lịch hẹn này? Không thể hoàn tác.")) return;
            try {
              await appointmentsApi.remove(a.id);
              qc.invalidateQueries({ queryKey: appointmentQueryKeys.all });
            } catch (err) {
              setError(apiError(err).detail);
            }
          }}
        >
          Xoá
        </Button>
      ),
    },
  ];

  const totalPages = list.data?.totalPages ?? 0;
  const totalElements = list.data?.totalElements ?? 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Lịch hẹn"
        description="Tất cả lịch hẹn theo khoảng ngày, bác sĩ, hoặc trạng thái."
        breadcrumbs={[{ label: "Trang chủ", to: "/dashboard" }, { label: "Lịch hẹn" }]}
        actions={
          <>
            <Button asChild variant="secondary">
              <Link to="/appointments">Lịch tuần</Link>
            </Button>
            <Button asChild variant="secondary">
              <Link to="/appointments/day">Lịch ngày</Link>
            </Button>
            <Button asChild>
              <Link to="/appointments/new">+ Đặt lịch</Link>
            </Button>
          </>
        }
      />

      {drill && (
        <DrillBanner
          drill={drill}
          total={list.data?.totalElements}
          doctorName={(id) => doctors.data?.find((d) => d.id === id)?.name}
        />
      )}

      {!drill && (
        <Toolbar
          search={{
            value: q,
            onChange: (v) => set({ q: v }),
            placeholder: "Tìm theo tên, số ĐT, bác sĩ…",
            autoFocus: true,
          }}
          filters={
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="filter-status" className="text-xs text-muted-foreground">
                  Trạng thái
                </Label>
                <Select
                  value={status || "ALL"}
                  onValueChange={(v) =>
                    set({ status: v === "ALL" ? "" : (v as AppointmentStatus) })
                  }
                >
                  <SelectTrigger
                    id="filter-status"
                    className="w-[180px]"
                    aria-label="Lọc trạng thái"
                  >
                    <SelectValue placeholder="Mọi trạng thái" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Mọi trạng thái</SelectItem>
                    {(Object.keys(appointmentStatusLabel) as AppointmentStatus[]).map((s) => (
                      <SelectItem key={s} value={s}>
                        {appointmentStatusLabel[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="filter-doctor" className="text-xs text-muted-foreground">
                  Bác sĩ
                </Label>
                <Select
                  value={doctorId || "ALL"}
                  onValueChange={(v) => set({ doctorId: v === "ALL" ? "" : v })}
                >
                  <SelectTrigger
                    id="filter-doctor"
                    className="w-[180px]"
                    aria-label="Lọc bác sĩ"
                  >
                    <SelectValue placeholder="Mọi bác sĩ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Mọi bác sĩ</SelectItem>
                    {doctors.data?.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="filter-range" className="text-xs text-muted-foreground">
                  Khoảng ngày
                </Label>
                <DateRangePicker
                  id="filter-range"
                  className="w-[260px]"
                  from={fromDate}
                  to={toDate}
                  onChange={({ from, to }) => set({ fromDate: from, toDate: to, date: "" })}
                />
              </div>
            </div>
          }
        />
      )}

      {error && (
        <div
          className="mb-3 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert"
        >
          {error}
        </div>
      )}

      <BulkDeleteBar
        count={sel.count}
        noun="lịch hẹn"
        pending={bulkPending}
        onClear={sel.clear}
        onDelete={async () => {
          setBulkPending(true);
          setError(null);
          const { failed } = await runBulkDelete(
            [...sel.selectedIds],
            appointmentsApi.remove
          );
          sel.clear();
          qc.invalidateQueries({ queryKey: appointmentQueryKeys.all });
          if (failed.length) setError(`${failed.length} mục không xoá được.`);
          setBulkPending(false);
        }}
      />

      <DataTable
        ariaLabel="Danh sách lịch hẹn"
        columns={columns}
        data={list.data?.content}
        loading={list.isLoading}
        error={list.error}
        selectedIds={sel.selectedIds}
        onSelectionChange={sel.setSelectedIds}
        onRowClick={(a) => navigate(`/appointments/${a.id}`)}
        empty={
          drill
            ? {
                title: "Không còn lịch hẹn nào trong tập này",
                description:
                  "Dữ liệu có thể đã thay đổi sau lúc con số được tính (huỷ, dời lịch, đổi trạng thái).",
              }
            : {
                title: "Không có lịch hẹn nào",
                description: "Thử mở rộng khoảng ngày hoặc bỏ bớt filter.",
              }
        }
        footer={
          <Pagination
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            pageSize={PAGE_SIZE}
            onChange={(p) => set({ page: p })}
          />
        }
      />
    </div>
  );
}

const KEY_LABEL: Record<DrillKey, string> = {
  status: "Trạng thái",
  channel: "Kênh",
  department: "Khoa",
  doctor: "Bác sĩ",
  branch: "Chi nhánh",
  room: "Phòng",
  service: "Dịch vụ",
  hour: "Giờ",
  dow: "Thứ",
};

const DOW_LABEL = ["", "Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7", "Chủ nhật"];

function keyValue(k: DrillKey, v: string, doctorName: (id: string) => string | undefined) {
  if (v === NONE) return "chưa gán";
  if (k === "status") return appointmentStatusLabel[v as AppointmentStatus] ?? v;
  if (k === "channel") return appointmentChannelLabel[v] ?? v;
  if (k === "hour") return `${v}h`;
  if (k === "dow") return DOW_LABEL[Number(v)] ?? v;
  if (k === "doctor") return doctorName(v) ?? "bác sĩ đã chọn";
  return "đã chọn";
}

/**
 * Dải đầu trang ở chế độ drill-down (#19863): nói rõ đây là tập bản ghi tạo nên MỘT con số, và
 * cách quay về danh sách thường. Bộ lọc tự do bị ẩn — đổi một điều kiện là không còn là tập đó nữa.
 */
function DrillBanner({
  drill,
  total,
  doctorName,
}: {
  drill: DrillQuery;
  total?: number;
  doctorName: (id: string) => string | undefined;
}) {
  const period =
    drill.fromDate === drill.toDate
      ? formatDate(drill.fromDate)
      : `${formatDate(drill.fromDate)} – ${formatDate(drill.toDate)}`;
  const keys = Object.entries(drill.keys ?? {}) as [DrillKey, string][];
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const exportCsv = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await drillDownApi.exportCsv(drill);
    } catch (e) {
      setExportError((e as Error).message);
    } finally {
      setExporting(false);
    }
  };
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-primary/30 bg-primary/5 px-4 py-3 text-sm"
      data-testid="drill-banner"
      role="status"
    >
      <div>
        <div className="font-medium">
          {drillMetricLabel[drill.metric]}
          {total != null && <span className="ml-1 tabular-nums">· {total} lịch</span>}
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          {period}
          {keys.map(([k, v]) => (
            <span key={k}>
              {" "}
              · {KEY_LABEL[k]}: {keyValue(k, v, doctorName)}
            </span>
          ))}{" "}
          · Danh sách các lịch hẹn tạo nên con số trên báo cáo.
        </div>
        {exportError && (
          <div className="mt-1 text-xs text-destructive" role="alert">
            {exportError}
          </div>
        )}
      </div>
      <div className="flex gap-2">
        {/* #19865: xuất phía server — danh sách có tên + SĐT bệnh nhân, nên có dòng kiểm toán. */}
        <Button
          variant="outline"
          size="sm"
          onClick={exportCsv}
          disabled={exporting || !total}
        >
          {exporting ? "Đang xuất…" : "Xuất CSV"}
        </Button>
        <Button asChild variant="outline" size="sm">
          <Link to="/appointments/list">Xem toàn bộ lịch hẹn</Link>
        </Button>
      </div>
    </div>
  );
}
